import { createSelector } from 'reselect'
import { RootState } from 'src/state/types'
import { BeneficiaryType } from 'types/finance'
import { invoiceApiSelectors } from '../invoice'
import { isBeneficiarySettled } from './utils'
import {
  TApplicationInvoiceStatus,
  TArchivedPeriod,
  TCustomerAccountActivity,
  TEasyPayData,
  TOpenInvoiceSummary,
  TReconInvoice,
  TReconNotification,
  TReconState,
} from './types'

export type InvoicePaymentBeneficiary = {
  partyId?: string
  beneficiaryId?: string
  amount?: number
  type: BeneficiaryType
}

/**
 * Selectors that call *other* modules' selectors need the whole store, so they take
 * `identity` as their dependency and cannot be memoised on a slice path.
 */
const state = (state: RootState): RootState => state

const reconState = (state: RootState): TReconState => state.api.recon

export const getInvoices = createSelector(reconState, (s): TReconInvoice[] => s.invoices)

export const getReconStatusByInvoiceId = createSelector(
  getInvoices,
  invoices =>
    (id: string | undefined): string =>
      (id && invoices.find(i => i.id === id)?.reconStatus) || '',
)

export const getActiveInvoicesByInvoiceStatus = createSelector(
  state,
  state => (invoiceStatus: string, portfolioId?: string) => {
    const filteredInvoices = getInvoices(state)
      .filter(inv => inv.reconStatus !== 'Reconciled')
      .filter(inv => inv.invoiceStatus === invoiceStatus)
      .filter(inv => !portfolioId || inv.portfolioId === portfolioId)

    return filteredInvoices.map(inv => ({
      ...inv,
      invoiceType: invoiceApiSelectors.getInvoiceTypeNameByValue(state)(inv.invoiceType),
    }))
  },
)

export const getInvoicesCount = createSelector(getInvoices, (invoices): number => invoices.length)

export const getCreditNoteReasons = createSelector(reconState, s => s.creditNoteReasons)

export const getInvoiceById = createSelector(
  getInvoices,
  invoices =>
    (id: string): TReconInvoice | undefined =>
      invoices.find(invoice => invoice.id === id),
)

/**
 * @returns the `partyId` of every party beneficiary on an invoice
 */
export const getInvoiceParties = createSelector(state, s => (id: string): string[] => {
  const invoice = getInvoiceById(s)(id)
  if (!invoice) return []
  return invoice.beneficiaries
    .filter(b => b.beneficiary.type === BeneficiaryType.PartyBeneficiary)
    .map(b => (b.beneficiary.value as { partyId: string }).partyId)
})

/**
 * @returns the EasyPay reference of every EasyPay beneficiary on an invoice
 */
export const getInvoiceEasyPayBeneficiaryReferences = createSelector(state, s => (id: string): string[] => {
  const invoice = getInvoiceById(s)(id)
  if (!invoice) return []
  return invoice.beneficiaries
    .filter(b => b.beneficiary.type === BeneficiaryType.EasyPayBeneficiary)
    .map(b => (b.beneficiary.value as { easyPayReference: string }).easyPayReference)
})

/**
 * An id that is not in the store contributes 0 rather than throwing on `.balance`,
 * which is what the previous `parseFloat(val.balance)` did.
 */
export const getInvoiceSelectionTotal = createSelector(
  state,
  state =>
    (ids: string[]): number =>
      ids.reduce((acc, id) => acc + (getInvoiceById(state)(id)?.balance ?? 0), 0),
)

export const getDoneInvoices = createSelector(state, state =>
  getInvoices(state)
    .filter(inv => inv.invoiceStatus === 'Done')
    .map(inv => ({
      ...inv,
      invoiceType: invoiceApiSelectors.getInvoiceTypeNameByValue(state)(inv.invoiceType),
    })),
)

export const getFilteredInvoices = createSelector(
  getInvoices,
  invoices =>
    (query: string): TReconInvoice[] =>
      invoices.filter(({ customerName, invoiceType }) =>
        `${customerName} ${invoiceType}`.toLowerCase().includes(query),
      ),
)

/**
 * Customer Summary
 */
export const getCustomerSummary = createSelector(
  reconState,
  rs =>
    (customerId: string, accountId: string): TCustomerAccountActivity | undefined =>
      rs.customerAccountActivity[customerId]?.[accountId],
)

/**
 * Invoice Activity
 */

export const getCustomerActiveInvoices = createSelector(
  state,
  s =>
    (customerId: string, accountId: string): TOpenInvoiceSummary[] =>
      getCustomerSummary(s)(customerId, accountId)?.openInvoices ?? [],
)

export const getCustomerRecentActivity = createSelector(
  state,
  s => (customerId: string, accountId: string) => getCustomerSummary(s)(customerId, accountId)?.recentActivity ?? [],
)

export const getCustomerActiveInvoicesTotal = createSelector(
  state,
  s =>
    (customerId: string, accountId: string): number =>
      getCustomerActiveInvoices(s)(customerId, accountId).reduce((total, o) => total + o.balance, 0),
)

/**
 * Notifications
 */
export const getNotifications = createSelector(reconState, reconState => reconState.notifications)

export const getNotificationByName = createSelector(
  getNotifications,
  notifications =>
    (name: string): TReconNotification | undefined =>
      notifications.find(n => n.name === name),
)

export const getNotificationCountByName = createSelector(
  state,
  state =>
    (name: string): number =>
      getNotificationByName(state)(name)?.count ?? 0,
)

export const getNotificationCountTotal = createSelector(state, (state): number => {
  const invoiceReadyNotifications: number = invoiceApiSelectors.getNotificationCountByName(state)('Ready')
  const invoiceNotSetNotifications: number = invoiceApiSelectors.getNotificationCountByName(state)('Not Set')
  const reconNotifications = getNotifications(state)

  return (
    reconNotifications.reduce((total, n) => total + n.count, 0) + invoiceReadyNotifications + invoiceNotSetNotifications
  )
})

/**
 * Customer Active invoices by account
 */
export const getCustomerActiveInvoicesByAccount = createSelector(
  reconState,
  reconState =>
    (customerId: string, accountId: string): TOpenInvoiceSummary[] =>
      reconState.customerActiveInvoicesByAccount[customerId]?.[accountId] ?? [],
)

/**
 * EasyPay
 */
export const getEasyPayData = createSelector(reconState, reconState => reconState.easypayData)

export const getEasyPayDataByReference = createSelector(
  reconState,
  reconState =>
    (reference: string): TEasyPayData =>
      reconState.easypayData[reference] ?? {},
)

/**
 * The ids of the unreconciled invoices on a portfolio, as written by
 * `invoicesActive_success`.
 */
export const getActiveInvoicesForPortfolio = createSelector(
  reconState,
  s =>
    (id: string): string[] =>
      s.activeInvoicesById[id] ?? [],
)

/**
 * @returns invoice beneficiaries flattened to the partyId / beneficiaryId / amount
 * shape used to build payment requests and resolve banking validation errors.
 *
 * `amount` is the beneficiary's outstanding balance, which is what recon expects to be
 * paid - not `beneficiary.value.amount`, the full amount the payment rule was set up for.
 *
 * Settled beneficiaries are left out: they have nothing outstanding, so recon rejects a
 * payment against them and the whole allocation fails with it. They also have no reason
 * to appear in the banking bank-details check, since nothing is being paid to them.
 */
export const getInvoicePaymentBeneficiaries = createSelector(
  state,
  s =>
    (invoiceId: string): InvoicePaymentBeneficiary[] => {
      const beneficiaries = getInvoiceById(s)(invoiceId)?.beneficiaries ?? []
      return beneficiaries
        .filter(
          ({ beneficiary, balance }) => !isBeneficiarySettled({ balance, originalAmount: beneficiary.value.amount }),
        )
        .map(({ beneficiary, balance }) => ({
          // EasyPay beneficiaries pay a reference, not a party, so they carry no partyId.
          partyId: 'partyId' in beneficiary.value ? beneficiary.value.partyId : undefined,
          beneficiaryId: beneficiary.value.id,
          // Pay what is still outstanding, not the full rule amount. A partially paid
          // beneficiary would otherwise be over-paid, and recon rejects the whole
          // allocation with it. This matches the single-invoice drawer, which submits
          // the beneficiary's balance.
          amount: balance ?? beneficiary.value.amount,
          type: beneficiary.type,
        }))
    },
)

/**
 * @returns beneficiaries in the shape required for events.payment_request
 */
export const getInvoiceBeneficiariesForPaymentRequest = createSelector(
  state,
  s => (invoiceId: string) =>
    getInvoicePaymentBeneficiaries(s)(invoiceId).map(({ beneficiaryId, amount }) => ({ beneficiaryId, amount })),
)

/**
 * Bulk invoices
 */
export const getBulkInvoiceUploadErrors = createSelector(reconState, s => s.bulkInvoiceUploadErrors)

/**
 * Application Fees
 */
export const getApplicationInvoicesStatusByPortfolioId = createSelector(
  reconState,
  state =>
    (portfolioId: string): TApplicationInvoiceStatus[] =>
      state.applicationInvoicesStatus[portfolioId] ?? [],
)

export const getArchivedPeriod = createSelector(reconState, (s): TArchivedPeriod => s.archivedPeriod)
